import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Users, Briefcase, Star, TrendingUp, FileText, Linkedin, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/Button';
export function HomePage() {
  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.6
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <div className="overflow-hidden" data-id="element-299" data-mp="31zlqx0ze5dok">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" data-id="element-300" data-mp="31yalkwqarxvc">
        <div className="absolute inset-0 z-0" data-id="element-301" data-mp="2e8zkfe51c1go">
          <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" alt="Team meeting" className="w-full h-full object-cover" data-id="element-302" data-mp="2e7ohfgjeyxqm" />
          <div className="absolute inset-0 bg-navy-900/80 mix-blend-multiply" data-id="element-303" data-mp="2eporfox5oko3" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center md:text-left" data-id="element-304" data-mp="2jgx2epe4we3d">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="max-w-3xl" data-id="element-305" data-mp="2of8bgxwlukkj">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 leading-tight" data-id="element-306" data-mp="20pxja4tblyi0">
              Connecting Top Talent with{' '}
              <span className="text-gold-500" data-id="element-307" data-mp="3c64bgib7sm2v">Opportunity</span>
            </h1>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl leading-relaxed" data-id="element-308" data-mp="3c88cmshqb6zg">
              We are a results-driven recruitment firm helping professionals
              secure high-quality roles and companies hire top-tier talent. Your
              success is our mission.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start" data-id="element-309" data-mp="2mjt6c56q1fex">
              <Button variant="gold" size="lg" href="/contact" data-id="element-310" data-mp="31cwp2fw1tz78">
                Get Started
              </Button>
              <Button variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-navy-900" href="/contact" data-id="element-311" data-mp="2we1tsegwai68">
                Book Free Consultation
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16 border-b border-gray-100" data-id="element-312" data-mp="2do3rkezs3jai">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-313" data-mp="1pysmilbeb2qk">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-314" data-mp="1l0zfxr66k6pr">
            {[{
            number: '500+',
            label: 'Successful Placements'
          }, {
            number: '98%',
            label: 'Client Satisfaction'
          }, {
            number: '50+',
            label: 'Industries Served'
          }, {
            number: '10+',
            label: 'Years Experience'
          }].map((stat, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="p-4" data-id="element-315" data-mp="j73mmij9nona" data-mpi={index}>
                <div className="text-4xl md:text-5xl font-bold text-navy-900 mb-2" data-id="element-316" data-mp="2z1vurva5xd69">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium" data-id="element-317" data-mp="3agqu23acirot">{stat.label}</div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* How It Works Preview */}
      <section className="py-20 bg-navy-900 text-white" data-id="element-318" data-mp="30iks5lojhljy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-319" data-mp="30k5mpbaut4ht">
          <div className="text-center mb-16" data-id="element-320" data-mp="21gr6symtifz">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-321" data-mp="205okwgxagqg">
              Your Path to Success
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto" data-id="element-322" data-mp="3o859k051oar4">
              A proven process designed to get you hired faster.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative" data-id="element-323" data-mp="3k6abd4ieudhk">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-navy-700 -z-10" data-id="element-324" data-mp="2cqnh3wswyxjw" />

            {[{
            step: '01',
            title: 'Consultation',
            desc: 'We analyze your goals and experience.'
          }, {
            step: '02',
            title: 'Strategy',
            desc: 'Custom roadmap and document prep.'
          }, {
            step: '03',
            title: 'Application',
            desc: 'Targeted submissions and networking.'
          }, {
            step: '04',
            title: 'Placement',
            desc: 'Interview support and offer negotiation.'
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} className="text-center" data-id="element-325" data-mp="1fypeg3gqccke" data-mpi={index}>
                <div className="w-24 h-24 bg-navy-800 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-navy-900 shadow-xl relative z-10" data-id="element-326" data-mp="oz99vmytavgj">
                  <span className="text-3xl font-bold text-gold-500" data-id="element-327" data-mp="xx1rwyvx4h7m">
                    {item.step}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-2" data-id="element-328" data-mp="1ct9do3mz5qp0">{item.title}</h3>
                <p className="text-gray-400" data-id="element-329" data-mp="17vrayuzjw7pg">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Success Stories — Real Career Wins */}
      <section className="py-24 bg-white relative overflow-hidden" data-id="element-330" data-mp="1hndiag8rpmmx">
        {/* Decorative background accents */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-gold-500/5 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" data-id="element-331" data-mp="1vm4uty7zf08y" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-navy-900/5 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl" data-id="element-332" data-mp="2a8kbqxjiq8jo" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-333" data-mp="2f7x05mq1fnod">
          {/* Header */}
          <div className="text-center mb-16" data-id="element-334" data-mp="20b5vaxxlhjrd">
            <span className="inline-block text-gold-600 font-semibold tracking-[0.25em] uppercase text-xs mb-4" data-id="element-335" data-mp="20cgxwufb0lgw">
              Real Career Wins
            </span>
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-navy-900 mb-4" data-id="element-336" data-mp="1rh17kndrcog2">
              Success Stories
            </h2>
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" data-id="element-337" data-mp="1wavs1t16t7g7" />
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-338" data-mp="1hl7nmixplso9">
              Behind every placement is a person whose career we helped
              transform. These are their stories.
            </p>
          </div>

          {/* Featured story — large card */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-navy-900 rounded-2xl overflow-hidden shadow-2xl mb-10 relative" data-id="element-339" data-mp="1clbksn0r07aa">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gold-500/10 rounded-full -translate-y-1/3 translate-x-1/3 blur-2xl" data-id="element-340" data-mp="1e28ua33fbgy4" />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-0" data-id="element-341" data-mp="1a39t8tktc4by">
              {/* Left: portrait + transformation */}
              <div className="lg:col-span-2 bg-navy-800 p-10 flex flex-col justify-center items-center text-center relative" data-id="element-342" data-mp="10bdr97flvu2g">
                <div className="w-28 h-28 rounded-full bg-gradient-to-br from-gold-500 to-gold-600 flex items-center justify-center text-white text-3xl font-serif font-bold mb-6 shadow-xl border-4 border-navy-900" data-id="element-343" data-mp="vdlotjhc4pk0">
                  MA
                </div>
                <div className="text-white font-bold text-xl mb-1" data-id="element-344" data-mp="2xz7x7tj2mcv1">
                  Michael Andrews
                </div>
                <div className="text-gold-500 text-sm mb-6" data-id="element-345" data-mp="3209ui4tfgeud">
                  Hired in 5 weeks
                </div>

                {/* Before / After */}
                <div className="w-full space-y-3 max-w-xs" data-id="element-346" data-mp="36xt8r4q4krmb">
                  <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10" data-id="element-347" data-mp="3bxxpmvuy3od2">
                    <div className="text-xs uppercase tracking-wider text-gray-400 mb-1" data-id="element-348" data-mp="2ecjmc7etztbg">
                      Before
                    </div>
                    <div className="text-white font-medium text-sm" data-id="element-349" data-mp="2j7z2j60ba1b1">
                      Regional Operations Manager
                    </div>
                    <div className="text-gray-400 text-xs" data-id="element-350" data-mp="2t16bt1y6a1ge">
                      Mid-size logistics firm
                    </div>
                  </div>
                  <div className="flex justify-center" data-id="element-351" data-mp="20ju0c2zayoec">
                    <ArrowRight className="h-5 w-5 text-gold-500 rotate-90" data-id="element-352" data-mp="20iiulxmar6kl" />
                  </div>
                  <div className="bg-gold-500/10 rounded-lg p-4 border border-gold-500/40" data-id="element-353" data-mp="2bq4khgpvejo5">
                    <div className="text-xs uppercase tracking-wider text-gold-500 mb-1" data-id="element-354" data-mp="2gofsrn0iom49">
                      After
                    </div>
                    <div className="text-white font-medium text-sm" data-id="element-355" data-mp="2qnwy23buwxzp">
                      VP of Operations
                    </div>
                    <div className="text-gray-300 text-xs" data-id="element-356" data-mp="1t0xynkts5by4">
                      Fortune 500 Logistics
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: story */}
              <div className="lg:col-span-3 p-10 lg:p-14 text-white relative" data-id="element-357" data-mp="22wrkxvxawjt3">
                <div className="flex items-center gap-3 mb-6" data-id="element-358" data-mp="27q44hrn4i0ez">
                  <span className="inline-flex items-center px-3 py-1 bg-gold-500 text-navy-900 text-xs font-bold uppercase tracking-wider rounded-full" data-id="element-359" data-mp="1a9x0gz0hhkfm">
                    Executive Search
                  </span>
                  <div className="flex text-gold-500" data-id="element-360" data-mp="3sjjefe8yyetb">
                    {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" data-id="element-361" data-mp="3oh69gs21qkxl" data-mpi={i} />)}
                  </div>
                </div>

                <h3 className="text-2xl md:text-3xl font-serif font-bold mb-6 leading-tight" data-id="element-362" data-mp="3jgidrt2vhfx1">
                  "After 18 months of dead-end searches, Continuum Talent
                  Solutions connected me to a role I never knew existed."
                </h3>

                <p className="text-gray-300 leading-relaxed mb-4" data-id="element-363" data-mp="34se1p7ybs2n8">
                  Michael had been quietly applying to executive roles for over
                  a year with no traction. Within two weeks of joining our
                  executive search program, our team had introduced him to three
                  hiring managers directly. The result? A VP role at a Fortune
                  500 company with a 42% compensation increase.
                </p>
                <p className="text-gray-400 text-sm italic" data-id="element-364" data-mp="2c6yyv8qnutk1">
                  "They didn't just find me a job — they architected the next
                  decade of my career."
                </p>
              </div>
            </div>
          </motion.div>

          {/* Supporting story cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-365" data-mp="277vxpzdsx7hc">
            {[{
            service: 'Resume Writing',
            serviceColor: 'bg-blue-100 text-blue-800',
            quote: 'Before Continuum Talent Solutions rewrote my resume, I was getting zero callbacks despite applying to 40+ jobs. Two weeks after the rewrite, I had 6 interviews scheduled and landed a Senior Engineer role at a top fintech company.',
            author: 'James Okonkwo',
            initials: 'JO',
            before: 'Software Engineer · Stuck for 8 months',
            after: 'Senior Engineer · Leading fintech',
            timeframe: '3 weeks'
          }, {
            service: 'Job Placement',
            serviceColor: 'bg-emerald-100 text-emerald-800',
            quote: 'As a returning mom after 4 years out of the workforce, I felt invisible to recruiters. Continuum Talent Solutions believed in me, repositioned my experience, and placed me in a marketing role I love — with a flexible schedule.',
            author: 'Rebecca Chen',
            initials: 'RC',
            before: 'Career gap · 4 years',
            after: 'Marketing Manager · Hybrid role',
            timeframe: '6 weeks'
          }, {
            service: 'Talent Sourcing',
            serviceColor: 'bg-purple-100 text-purple-800',
            quote: 'A Continuum Talent Solutions recruiter reached out about a role I had never seen advertised. It turned out to be the perfect fit — a healthcare leadership position aligned with my values. I never would have found it on my own.',
            author: 'Dr. Priya Nair',
            initials: 'PN',
            before: 'Senior Nurse Practitioner',
            after: 'Director of Clinical Operations',
            timeframe: '30 days'
          }].map((story, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white border border-gray-200 rounded-2xl p-7 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col" data-id="element-366" data-mp="s6djblg36ytx" data-mpi={index}>
                {/* Service tag + rating */}
                <div className="flex items-center justify-between mb-5" data-id="element-367" data-mp="1uyptxdzkrxrv">
                  <span className={`inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full ${story.serviceColor}`} data-id="element-368" data-mp="1v00z9i8o5dl3">
                    {story.service}
                  </span>
                  <div className="flex text-gold-500" data-id="element-369" data-mp="1gz83yvajmegx">
                    {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-current" data-id="element-370" data-mp="2dpqug6gcg1dj" data-mpi={i} />)}
                  </div>
                </div>

                {/* Quote */}
                <p className="text-gray-700 leading-relaxed mb-6 flex-grow text-[15px]" data-id="element-371" data-mp="23ze94c0pg46c">
                  "{story.quote}"
                </p>

                {/* Before → After */}
                <div className="bg-gray-50 rounded-lg p-4 mb-5 border border-gray-100" data-id="element-372" data-mp="1zvpzzowfd0iz">
                  <div className="flex items-start gap-2 mb-2" data-id="element-373" data-mp="2wh03kem7co4l">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mt-0.5 w-12 flex-shrink-0" data-id="element-374" data-mp="2rnxi6trkwkzh">
                      From
                    </span>
                    <span className="text-sm text-gray-600" data-id="element-375" data-mp="ur2mj4zzd2r9">
                      {story.before}
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-376" data-mp="g1d5e0bhso5c">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gold-600 mt-0.5 w-12 flex-shrink-0" data-id="element-377" data-mp="kzy7y6e7vjwy">
                      To
                    </span>
                    <span className="text-sm text-navy-900 font-semibold" data-id="element-378" data-mp="bzrxyuhq922j">
                      {story.after}
                    </span>
                  </div>
                </div>

                {/* Author */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100" data-id="element-379" data-mp="21c62pavlorz">
                  <div className="flex items-center gap-3" data-id="element-380" data-mp="1rgab6lvwbrze">
                    <div className="w-10 h-10 rounded-full bg-navy-900 text-white flex items-center justify-center font-bold text-sm" data-id="element-381" data-mp="1reph0xdhub22">
                      {story.initials}
                    </div>
                    <div data-id="element-382" data-mp="3gvulpzocc3rk">
                      <div className="font-bold text-navy-900 text-sm" data-id="element-383" data-mp="3bz4apqre7yd0">
                        {story.author}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-384" data-mp="3qoikgzyvshsx">
                        Verified Client
                      </div>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-385" data-mp="3i9gt0skpob2">
                    <div className="text-xs text-gray-400 uppercase tracking-wider" data-id="element-386" data-mp="3l402ozup32w">
                      Hired in
                    </div>
                    <div className="text-sm font-bold text-gold-600" data-id="element-387" data-mp="dhqil74pfi1r">
                      {story.timeframe}
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>

          {/* CTA */}
          <div className="text-center mt-14" data-id="element-388" data-mp="2fao9gxex80k2">
            <Button variant="outline" href="/testimonials" data-id="element-389" data-mp="2aaab6734g4z8">
              View All Success Stories
            </Button>
          </div>
        </div>
      </section>

      {/* For Employers CTA */}
      <section className="py-20 bg-gray-50" data-id="element-390" data-mp="1temmgl9kwvpu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-391" data-mp="1tdl744lj5n2b">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-id="element-392" data-mp="1y75znh4ctou4">
            <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-393" data-mp="235r0nirfkcjm">
              <div className="p-12 flex flex-col justify-center" data-id="element-394" data-mp="237ael2pm6bi8">
                <span className="text-gold-600 font-bold tracking-wider uppercase text-sm mb-2" data-id="element-395" data-mp="15iqwbrul4uym">
                  For Employers
                </span>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy-900 mb-6" data-id="element-396" data-mp="1ffn05k9jgswu">
                  Hire Top Talent Today
                </h2>
                <p className="text-gray-600 mb-8 text-lg" data-id="element-397" data-mp="1kgax0mkg83yz">
                  Stop sifting through unqualified resumes. We deliver
                  pre-screened, top-tier candidates ready to make an impact on
                  day one.
                </p>
                <ul className="space-y-4 mb-8" data-id="element-398" data-mp="k71gentyyg3w">
                  {['Pre-screened candidates', 'Industry-specific expertise', 'Replacement guarantee'].map((item, i) => <li key={i} className="flex items-center text-gray-700" data-id="element-399" data-mp="aevspqar2kui" data-mpi={i}>
                      <CheckCircle className="h-5 w-5 text-gold-500 mr-3" data-id="element-400" data-mp="5gu9949ewjii" />
                      {item}
                    </li>)}
                </ul>
                <div data-id="element-401" data-mp="3sltkouvjsz1n">
                  <Button variant="primary" href="/about#team" data-id="element-402" data-mp="3sla69o53wssd">
                    Find Talent Now
                  </Button>
                </div>
              </div>
              <div className="relative h-64 lg:h-auto" data-id="element-403" data-mp="3iof5szp9wibg">
                <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Business handshake" className="absolute inset-0 w-full h-full object-cover" data-id="element-404" data-mp="3ipzyejs11r6o" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-navy-900 text-center px-4" data-id="element-405" data-mp="23saaodi6uvb3">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="max-w-4xl mx-auto" data-id="element-406" data-mp="1z0a5a5jcna74">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6" data-id="element-407" data-mp="1l112c7k5mcl3">
            Ready to Land Your Dream Job?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-408" data-mp="1g2g0k3p97kuj">
            Join thousands of professionals who have accelerated their careers
            with Continuum Talent Solutions.
          </p>
          <Button variant="gold" size="lg" href="/contact" data-id="element-409" data-mp="3ae4huciyjzi3">
            Start Your Journey
          </Button>
        </motion.div>
      </section>
    </div>;
}